import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Star, Loader2, DollarSign, Users, Activity, Settings2, PauseCircle, PlayCircle, XCircle, RotateCcw, ArrowLeftRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const INTERVALS = ["monthly", "quarterly", "annual", "one-time"];
const STATUSES = ["active", "pending", "paused", "canceled"];
const STRIPE_PLANS = [
  { lookup_key: "founding_athlete_monthly", label: "Founding Athlete — $125/mo" },
  { lookup_key: "foundation_monthly", label: "Foundation — $225/mo" },
  { lookup_key: "accelerate_monthly", label: "Accelerate — $300/mo" },
  { lookup_key: "online_coaching_monthly", label: "Online Coaching — $175/mo" },
];
const inputCls =
  "w-full bg-[#0A0A0A] border border-white/10 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";
const labelCls =
  "font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5";

export default function MembershipsTab() {
  const [plans, setPlans] = useState([]);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPlan, setShowPlan] = useState(false);
  const [showMember, setShowMember] = useState(false);

  const emptyPlan = { name: "", price: "", interval: "monthly", membership_type: "in_person", description: "" };
  const emptyMember = {
    member_name: "", email: "", phone: "", plan_id: "", price: "",
    interval: "monthly", status: "active", founding_athlete: false,
  };
  const [planForm, setPlanForm] = useState(emptyPlan);
  const [memberForm, setMemberForm] = useState(emptyMember);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m, s] = await Promise.all([
        api.get("/plans"),
        api.get("/memberships"),
        api.get("/memberships/stats"),
      ]);
      setPlans(p.data);
      setMembers(m.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load memberships.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePlan = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/plans", { ...planForm, price: parseFloat(planForm.price) || 0 });
      toast.success("Plan created.");
      setPlanForm(emptyPlan);
      setShowPlan(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (id) => {
    try {
      await api.delete(`/plans/${id}`);
      setPlans((prev) => prev.filter((p) => p.id !== id));
      toast.success("Plan deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const onSelectPlan = (planId) => {
    const p = plans.find((x) => x.id === planId);
    setMemberForm((f) => ({
      ...f,
      plan_id: planId,
      price: p ? p.price : f.price,
      interval: p ? p.interval : f.interval,
    }));
  };

  const saveMember = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const plan = plans.find((x) => x.id === memberForm.plan_id);
      await api.post("/memberships", {
        ...memberForm,
        email: memberForm.email || null,
        phone: memberForm.phone || null,
        plan_name: plan ? plan.name : null,
        price: parseFloat(memberForm.price) || 0,
      });
      toast.success("Member added.");
      setMemberForm(emptyMember);
      setShowMember(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setMemberStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/memberships/${id}`, { status });
      setMembers((prev) => prev.map((m) => (m.id === id ? data : m)));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteMember = async (id) => {
    try {
      await api.delete(`/memberships/${id}`);
      setMembers((prev) => prev.filter((m) => m.id !== id));
      toast.success("Member removed.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const [manageId, setManageId] = useState(null);
  const [changePlan, setChangePlan] = useState({});
  const [busy, setBusy] = useState(false);

  const subAction = async (id, fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doChangePlan = (m) => subAction(m.id, async () => {
    const lk = changePlan[m.id];
    if (!lk) { throw { response: { data: { detail: "Select a plan first." } } }; }
    await api.post(`/admin/memberships/${m.id}/change-plan`, { lookup_key: lk });
    toast.success("Plan changed (prorated).");
  });
  const doPause = (m) => subAction(m.id, async () => { await api.post(`/admin/memberships/${m.id}/pause`); toast.success("Membership paused."); });
  const doResume = (m) => subAction(m.id, async () => { await api.post(`/admin/memberships/${m.id}/resume`); toast.success("Membership resumed."); });
  const doCancel = (m, atPeriodEnd) => subAction(m.id, async () => {
    await api.post(`/admin/memberships/${m.id}/cancel`, { at_period_end: atPeriodEnd });
    toast.success(atPeriodEnd ? "Will cancel at period end." : "Canceled immediately.");
  });
  const doRefund = (m) => {
    const raw = window.prompt("Refund amount in $ (leave blank for full refund of last payment):", "");
    if (raw === null) return;
    const amount = raw.trim() === "" ? null : parseFloat(raw);
    subAction(m.id, async () => {
      await api.post(`/admin/memberships/${m.id}/refund`, { amount });
      toast.success("Refund issued.");
    });
  };

  const foundingFull = stats && stats.founding_remaining <= 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-white/40">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="memberships-tab">
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-8">
        Membership Management
      </h1>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-12">
          {[
            { icon: Users, label: "Total Members", value: stats.total_members },
            { icon: DollarSign, label: "Est. MRR", value: `$${stats.mrr.toLocaleString()}` },
            { icon: Activity, label: "Active", value: stats.by_status.active },
            { icon: Star, label: "Founding Left", value: `${stats.founding_remaining}/${stats.founding_limit}` },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-[#0A0A0A] p-6" data-testid={`ms-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                <Icon size={18} className="text-white/70 mb-3" />
                <p className="font-mono-accent text-3xl font-bold">{s.value}</p>
                <p className="text-white/40 text-xs uppercase tracking-wide mt-1">{s.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* PLANS */}
      <section className="mb-14">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold uppercase tracking-tight text-2xl">Plans</h2>
          <button
            data-testid="toggle-add-plan"
            onClick={() => setShowPlan((v) => !v)}
            className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"
          >
            {showPlan ? <X size={14} /> : <Plus size={14} />} {showPlan ? "Cancel" : "Add Plan"}
          </button>
        </div>

        {showPlan && (
          <form onSubmit={savePlan} className="bg-[#121212] border border-white/10 p-6 mb-6 grid sm:grid-cols-2 gap-4" data-testid="plan-form">
            <div>
              <label className={labelCls}>Plan Name</label>
              <input data-testid="plan-name" required value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Price ($)</label>
              <input data-testid="plan-price" type="number" step="0.01" min="0" required value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Billing Interval</label>
              <select data-testid="plan-interval" value={planForm.interval} onChange={(e) => setPlanForm({ ...planForm, interval: e.target.value })} className={inputCls}>
                {INTERVALS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Membership Type</label>
              <select data-testid="plan-type" value={planForm.membership_type} onChange={(e) => setPlanForm({ ...planForm, membership_type: e.target.value })} className={inputCls}>
                {["in_person", "digital", "team", "camp", "hybrid"].map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <input data-testid="plan-description" value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <button data-testid="plan-submit" disabled={saving} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Save Plan
              </button>
            </div>
          </form>
        )}

        {plans.length === 0 ? (
          <p className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="plans-empty">No plans yet. Add your pricing plans above.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-list">
            {plans.map((p) => (
              <div key={p.id} data-testid={`plan-${p.id}`} className="bg-[#121212] border border-white/10 p-5 flex justify-between items-start">
                <div>
                  <h3 className="font-display font-bold uppercase tracking-tight text-lg">{p.name}</h3>
                  <p className="font-mono-accent text-2xl font-bold mt-1">${p.price}<span className="text-white/40 text-xs font-normal">/{p.interval}</span></p>
                  {p.description && <p className="text-white/50 text-xs mt-2">{p.description}</p>}
                </div>
                <button data-testid={`plan-delete-${p.id}`} onClick={() => deletePlan(p.id)} className="text-white/30 hover:text-red-400 transition-colors">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* MEMBERS */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold uppercase tracking-tight text-2xl">Members</h2>
          <button
            data-testid="toggle-add-member"
            onClick={() => setShowMember((v) => !v)}
            className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"
          >
            {showMember ? <X size={14} /> : <Plus size={14} />} {showMember ? "Cancel" : "Add Member"}
          </button>
        </div>

        {showMember && (
          <form onSubmit={saveMember} className="bg-[#121212] border border-white/10 p-6 mb-6 grid sm:grid-cols-2 gap-4" data-testid="member-form">
            <div>
              <label className={labelCls}>Member Name</label>
              <input data-testid="member-name" required value={memberForm.member_name} onChange={(e) => setMemberForm({ ...memberForm, member_name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input data-testid="member-email" type="email" value={memberForm.email} onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input data-testid="member-phone" value={memberForm.phone} onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Plan</label>
              <select data-testid="member-plan" value={memberForm.plan_id} onChange={(e) => onSelectPlan(e.target.value)} className={inputCls}>
                <option value="">— None / Custom —</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name} (${p.price}/{p.interval})</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Price ($)</label>
              <input data-testid="member-price" type="number" step="0.01" min="0" value={memberForm.price} onChange={(e) => setMemberForm({ ...memberForm, price: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Interval</label>
              <select data-testid="member-interval" value={memberForm.interval} onChange={(e) => setMemberForm({ ...memberForm, interval: e.target.value })} className={inputCls}>
                {INTERVALS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select data-testid="member-status" value={memberForm.status} onChange={(e) => setMemberForm({ ...memberForm, status: e.target.value })} className={inputCls}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label className={`flex items-center gap-2 text-sm ${foundingFull && !memberForm.founding_athlete ? "opacity-40" : ""}`}>
                <input
                  data-testid="member-founding"
                  type="checkbox"
                  disabled={foundingFull && !memberForm.founding_athlete}
                  checked={memberForm.founding_athlete}
                  onChange={(e) => setMemberForm({ ...memberForm, founding_athlete: e.target.checked })}
                  className="accent-white w-4 h-4"
                />
                <span className="flex items-center gap-1"><Star size={14} /> Founding Athlete {foundingFull && "(full)"}</span>
              </label>
            </div>
            <div className="sm:col-span-2">
              <button data-testid="member-submit" disabled={saving} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Save Member
              </button>
            </div>
          </form>
        )}

        {members.length === 0 ? (
          <p className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="members-empty">No members yet.</p>
        ) : (
          <div className="space-y-3" data-testid="members-list">
            {members.map((m) => (
              <div key={m.id} data-testid={`member-${m.id}`} className="bg-[#121212] border border-white/10 hover:border-white/25 transition-colors p-5">
               <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display font-bold uppercase tracking-tight text-xl">{m.member_name}</h3>
                    {m.founding_athlete && (
                      <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-white px-2 py-0.5 flex items-center gap-1">
                        <Star size={11} /> Founding
                      </span>
                    )}
                    {m.stripe_subscription_id && (
                      <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-white/25 text-white/50 px-2 py-0.5">Stripe</span>
                    )}
                  </div>
                  <div className="flex gap-x-6 gap-y-1 flex-wrap text-sm text-white/50 mt-1.5">
                    {m.plan_name && <span>{m.plan_name}</span>}
                    <span className="font-mono-accent text-white/70">${m.price}/{m.interval}</span>
                    {m.email && <span>{m.email}</span>}
                    {m.phone && <span>{m.phone}</span>}
                    {m.current_period_end && <span className="text-white/40">Next: {new Date(m.current_period_end).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {m.stripe_subscription_id && (
                    <button
                      data-testid={`member-manage-${m.id}`}
                      onClick={() => setManageId(manageId === m.id ? null : m.id)}
                      className="inline-flex items-center gap-2 border border-white/20 px-3 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"
                    >
                      <Settings2 size={14} /> {manageId === m.id ? "Close" : "Manage"}
                    </button>
                  )}
                  <select
                    data-testid={`member-status-${m.id}`}
                    value={m.status}
                    onChange={(e) => setMemberStatus(m.id, e.target.value)}
                    disabled={!!m.stripe_subscription_id}
                    title={m.stripe_subscription_id ? "Managed via Stripe — use the Manage panel to pause, resume or cancel" : ""}
                    className="bg-[#0A0A0A] border border-white/15 text-xs uppercase tracking-widest px-3 py-2 outline-none focus:border-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button data-testid={`member-delete-${m.id}`} onClick={() => deleteMember(m.id)} className="text-white/30 hover:text-red-400 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
               </div>

               {manageId === m.id && m.stripe_subscription_id && (
                <div className="mt-5 pt-5 border-t border-white/10" data-testid={`manage-panel-${m.id}`}>
                  <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-3">Subscription Actions (Stripe)</p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-end mb-4">
                    <div className="flex-1 max-w-xs">
                      <label className={labelCls}>Change Plan (prorated)</label>
                      <select
                        data-testid={`change-plan-select-${m.id}`}
                        value={changePlan[m.id] || ""}
                        onChange={(e) => setChangePlan((p) => ({ ...p, [m.id]: e.target.value }))}
                        className={inputCls}
                      >
                        <option value="">Select new plan…</option>
                        {STRIPE_PLANS.map((p) => <option key={p.lookup_key} value={p.lookup_key}>{p.label}</option>)}
                      </select>
                    </div>
                    <button data-testid={`change-plan-btn-${m.id}`} onClick={() => doChangePlan(m)} disabled={busy || !changePlan[m.id]}
                      className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 hover:bg-white/85 transition-colors disabled:opacity-50">
                      <ArrowLeftRight size={14} /> Apply
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {m.status === "paused" ? (
                      <button data-testid={`resume-btn-${m.id}`} onClick={() => doResume(m)} disabled={busy}
                        className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors disabled:opacity-50">
                        <PlayCircle size={14} /> Resume
                      </button>
                    ) : (
                      <button data-testid={`pause-btn-${m.id}`} onClick={() => doPause(m)} disabled={busy}
                        className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors disabled:opacity-50">
                        <PauseCircle size={14} /> Pause
                      </button>
                    )}
                    <button data-testid={`cancel-period-btn-${m.id}`} onClick={() => doCancel(m, true)} disabled={busy}
                      className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors disabled:opacity-50">
                      <RotateCcw size={14} /> Cancel at Period End
                    </button>
                    <button data-testid={`cancel-now-btn-${m.id}`} onClick={() => { if (window.confirm("Cancel immediately? This stops access now.")) doCancel(m, false); }} disabled={busy}
                      className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-red-500 hover:border-red-500 hover:text-white transition-colors disabled:opacity-50">
                      <XCircle size={14} /> Cancel Now
                    </button>
                    <button data-testid={`refund-btn-${m.id}`} onClick={() => doRefund(m)} disabled={busy}
                      className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors disabled:opacity-50">
                      <DollarSign size={14} /> Refund
                    </button>
                  </div>
                </div>
               )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
